/**
 * Knowledge Base API hooks — CRUD для базы знаний.
 *
 * Phase 7: Knowledge Base Management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { client } from './client';

// ============================================================================
// Types
// ============================================================================

export type KnowledgeItemKind = 'qa' | 'note';
export type EmbeddingStatus = 'pending' | 'processing' | 'done' | 'failed';

export interface KnowledgeItem {
  id: string;
  orgId: string;
  title: string;
  content: string;
  kind: KnowledgeItemKind;
  isActive: boolean;
  sourceType: string;
  createdBy: string;
  embeddingStatus: EmbeddingStatus;
  version: number;
  category: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateKnowledgeParams {
  title: string;
  content: string;
  kind?: KnowledgeItemKind;
  category?: string | null;
}

export interface UpdateKnowledgeParams {
  title?: string;
  content?: string;
  kind?: KnowledgeItemKind;
  isActive?: boolean;
  category?: string | null;
}

// ============================================================================
// Hooks
// ============================================================================

export function useKnowledgeItems(filters?: { isActive?: boolean; kind?: KnowledgeItemKind }) {
  return useQuery({
    queryKey: ['knowledge', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive));
      if (filters?.kind) params.set('kind', filters.kind);
      const qs = params.toString();
      const { data } = await client.get<{ items: KnowledgeItem[] }>(
        `/knowledge${qs ? `?${qs}` : ''}`
      );
      return data.items;
    },
  });
}

export function useCreateKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: CreateKnowledgeParams) => {
      const { data } = await client.post<{ item: KnowledgeItem }>('/knowledge', params);
      return data.item;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge'] }),
  });
}

export function useUpdateKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...params }: UpdateKnowledgeParams & { id: string }) => {
      const { data } = await client.patch<{ item: KnowledgeItem }>(`/knowledge/${id}`, params);
      return data.item;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge'] }),
  });
}

export function useDeleteKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.delete(`/knowledge/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge'] }),
  });
}
